import React, { FC, useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiSpacer, EuiTitle, EuiLoadingContent, EuiText } from '@elastic/eui'

import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { Persona } from 'api/interfaces/Persona'
import { useGetMasterCampaignPlanPersonaSectionQuery, useUpdatePersonaIntroductionSectionMutation, useHidePersonaSectionMutation, useAddPersonaCustomSectionMutation, useUpdatePersonaCustomSectionMutation, useDeletePersonaCustomSectionMutation, useUpdatePersonaPageTitleMutation } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { RootState } from 'app/rootReducer'
import { AcConfirmModal } from 'components/Modals/AcConfirmModal'
import { AddCustomSectionButton } from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/AddCustomSectionButton'
import { downloadPdf } from 'utils/pdfDownloader'

import { selectEditMode } from '../../masterCampaignPlanSlice'
import CustomizableSection from '../ExecutiveSummary/CustomizableSection'
import EditCustomSectionFlyout from '../ExecutiveSummary/EditCustomSectionFlyout'
import EditPageTitleFlyout from '../ExecutiveSummary/EditPageTitleFlyout'
import { RenderCustomSections } from '../shared/RenderCustomSections'
import PersonaList from './PersonaList'
import PersonaPageHeader from './PersonaPageHeader'
import PersonasPdf from './PersonasPdf'

type SectionType = 'introduction'
export type PersonaCustomSectionPosition = 'abovePersonaList'

export interface PersonasProps {
  masterCampaignPlan: MasterCampaignPlan
  onEditPersonaClick?: (persona: Persona) => void
  onAddPersonaClick?: () => void
  onReorderPersonasClick?: () => void
  onDeletePersonaClick?: (persona: Persona) => Promise<void>
  onDeletePersonaLoading?: boolean
  isFetching?: boolean
  onAllClientPdfsClick?: () => void
  onAllStaffPdfsClick?: () => void
  staffPdfDisabledReason?: string
}

const Personas: FC<PersonasProps> = ({ masterCampaignPlan, onEditPersonaClick, onAddPersonaClick, onReorderPersonasClick, onDeletePersonaClick, onDeletePersonaLoading = false, isFetching = false, onAllClientPdfsClick, onAllStaffPdfsClick, staffPdfDisabledReason }) => {
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const editMode = useSelector(selectEditMode)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const personas = masterCampaignPlan.personas || []

  const [editingSectionType, setEditingSectionType] = useState<SectionType | null>(null)
  const [deletingSectionType, setDeletingSectionType] = useState<SectionType | null>(null)
  const [addingCustomSectionPosition, setAddingCustomSectionPosition] = useState<PersonaCustomSectionPosition | null>(null)
  const [editingCustomSection, setEditingCustomSection] = useState<{ position: PersonaCustomSectionPosition; index: number } | null>(null)
  const [deletingCustomSection, setDeletingCustomSection] = useState<{ position: PersonaCustomSectionPosition; index: number } | null>(null)
  const [editingPageTitle, setEditingPageTitle] = useState(false)

  const { data: personaSection } = useGetMasterCampaignPlanPersonaSectionQuery({
    masterCampaignPlanId: masterCampaignPlan.id
  })

  const [updateIntroduction, { isLoading: isUpdatingIntroduction }] = useUpdatePersonaIntroductionSectionMutation()
  const [hideSection, { isLoading: isHidingSection }] = useHidePersonaSectionMutation()
  const [addCustomSection, { isLoading: isAddingCustomSection }] = useAddPersonaCustomSectionMutation()
  const [updateCustomSection, { isLoading: isUpdatingCustomSection }] = useUpdatePersonaCustomSectionMutation()
  const [deleteCustomSection, { isLoading: isDeletingCustomSection }] = useDeletePersonaCustomSectionMutation()
  const [updatePageTitle, { isLoading: isUpdatingPageTitle }] = useUpdatePersonaPageTitleMutation()

  const handleEditSection = (sectionType: SectionType) => {
    setEditingSectionType(sectionType)
  }

  const handleDeleteSection = (sectionType: SectionType) => {
    setDeletingSectionType(sectionType)
  }

  const handleSaveSection = async (title: string, content: string, showTitle: boolean) => {
    if (!editingSectionType) return

    await updateIntroduction({
      masterCampaignPlanId: masterCampaignPlan.id,
      title,
      content,
      showTitle
    })

    setEditingSectionType(null)
  }

  const confirmDeleteSection = async () => {
    if (!deletingSectionType) return

    await hideSection({
      masterCampaignPlanId: masterCampaignPlan.id,
      sectionType: deletingSectionType
    })

    setDeletingSectionType(null)
  }

  const handleAddCustomSection = (position: PersonaCustomSectionPosition) => {
    setAddingCustomSectionPosition(position)
  }

  const handleEditCustomSection = (position: PersonaCustomSectionPosition, index: number) => {
    setEditingCustomSection({ position, index })
  }

  const handleDeleteCustomSection = (position: PersonaCustomSectionPosition, index: number) => {
    setDeletingCustomSection({ position, index })
  }

  const handleSaveCustomSection = async (title: string, content: string, showTitle: boolean) => {
    if (addingCustomSectionPosition) {
      const existingSections = personaSection?.[addingCustomSectionPosition] || []
      await addCustomSection({
        masterCampaignPlanId: masterCampaignPlan.id,
        position: addingCustomSectionPosition,
        existingSections,
        title,
        content,
        showTitle
      })
      setAddingCustomSectionPosition(null)
    } else if (editingCustomSection) {
      const existingSections = personaSection?.[editingCustomSection.position] || []
      await updateCustomSection({
        masterCampaignPlanId: masterCampaignPlan.id,
        position: editingCustomSection.position,
        existingSections,
        sectionIndex: editingCustomSection.index,
        title,
        content,
        showTitle
      })
      setEditingCustomSection(null)
    }
  }

  const confirmDeleteCustomSection = async () => {
    if (!deletingCustomSection) return

    const existingSections = personaSection?.[deletingCustomSection.position] || []
    await deleteCustomSection({
      masterCampaignPlanId: masterCampaignPlan.id,
      position: deletingCustomSection.position,
      existingSections,
      sectionIndex: deletingCustomSection.index
    })

    setDeletingCustomSection(null)
  }

  const handleEditPageTitle = () => {
    setEditingPageTitle(true)
  }

  const handleSavePageTitle = async (newTitle: string) => {
    await updatePageTitle({
      masterCampaignPlanId: masterCampaignPlan.id,
      pageTitle: newTitle
    })
    setEditingPageTitle(false)
  }

  const getDefaultContent = (): string => {
    return 'The following personas are descriptive of the types of people we will be targeting. While fictional generalizations, they are based on research and market analysis. They will be used both in the targeting of your ad campaigns and in the crafting of your ads.'
  }

  const getDeleteCustomSectionTitle = (): string => {
    if (!deletingCustomSection) return 'Delete Custom Section'

    const sections = personaSection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Delete ${title}`
    }

    return 'Delete Custom Section'
  }

  const getDeleteCustomSectionMessage = (): string => {
    if (!deletingCustomSection) return 'Are you sure you want to delete this custom section?'

    const sections = personaSection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Are you sure you want to delete ${title}?`
    }

    return 'Are you sure you want to delete this custom section?'
  }

  const isUpdatingAnySection = isUpdatingIntroduction || isAddingCustomSection || isUpdatingCustomSection

  const handlePdfDownload = async () => {
    try {
      setIsGeneratingPdf(true)

      const filename = `${masterCampaignPlan.name} - Personas.pdf`

      await downloadPdf(<PersonasPdf masterCampaignPlan={masterCampaignPlan} accountName={currentAccount?.name || ''} personaSection={personaSection} />, filename)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  if (isFetching) {
    return (
      <>
        <EuiTitle size='m'>
          <h3>Persona Prioritization</h3>
        </EuiTitle>
        <EuiSpacer size='m' />
        <EuiLoadingContent lines={10} />
      </>
    )
  }

  return (
    <>
      <PersonaPageHeader pageTitle={personaSection?.pageTitle} onEditPageTitle={handleEditPageTitle} showActions={editMode} onClientPdfClick={handlePdfDownload} isGeneratingPdf={isGeneratingPdf} onAllClientPdfsClick={onAllClientPdfsClick} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} />

      {!personaSection?.introduction?.hideAll && (
        <>
          <CustomizableSection section={personaSection?.introduction} defaultTitle='' defaultContent={getDefaultContent()} defaultShowTitle={false} separator='spacer' onEdit={() => handleEditSection('introduction')} onDelete={() => handleDeleteSection('introduction')} showActions={editMode} />
          <EuiSpacer size='xl' />
        </>
      )}

      <RenderCustomSections sections={personaSection?.abovePersonaList} position='abovePersonaList' editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {editMode && (
        <>
          <AddCustomSectionButton position='abovePersonaList' onClick={handleAddCustomSection} />
          <EuiSpacer size='xl' />
        </>
      )}

      <PersonaList masterCampaignPlanStatus={masterCampaignPlan.status} personas={personas} onEditPersonaClick={editMode ? onEditPersonaClick : undefined} onAddPersonaClick={editMode ? onAddPersonaClick : undefined} onReorderPersonasClick={editMode ? onReorderPersonasClick : undefined} onDeletePersonaClick={editMode ? onDeletePersonaClick : undefined} onDeletePersonaLoading={onDeletePersonaLoading} showActions={editMode} />

      {editingSectionType && <EditCustomSectionFlyout section={personaSection?.introduction} defaultTitle='' defaultContent={getDefaultContent()} defaultShowTitle={false} flyoutTitle='Edit Introduction' onSave={handleSaveSection} onClose={() => setEditingSectionType(null)} isLoading={isUpdatingAnySection} />}

      {(addingCustomSectionPosition || editingCustomSection) && (
        <EditCustomSectionFlyout
          section={editingCustomSection ? personaSection?.[editingCustomSection.position]?.[editingCustomSection.index] : undefined}
          defaultTitle=''
          defaultContent=''
          defaultShowTitle={true}
          flyoutTitle={addingCustomSectionPosition ? 'Add Custom Section' : 'Edit Custom Section'}
          onSave={handleSaveCustomSection}
          onClose={() => {
            setAddingCustomSectionPosition(null)
            setEditingCustomSection(null)
          }}
          isLoading={isUpdatingAnySection}
        />
      )}

      {deletingSectionType && <AcConfirmModal message='Are you sure you want to delete this section?' title='Delete Introduction' onCancel={() => setDeletingSectionType(null)} onConfirm={confirmDeleteSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isHidingSection} confirmButtonDisabled={isHidingSection} />}

      {deletingCustomSection && <AcConfirmModal message={getDeleteCustomSectionMessage()} title={getDeleteCustomSectionTitle()} onCancel={() => setDeletingCustomSection(null)} onConfirm={confirmDeleteCustomSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isDeletingCustomSection} confirmButtonDisabled={isDeletingCustomSection} />}

      {editingPageTitle && <EditPageTitleFlyout currentTitle={personaSection?.pageTitle || 'Persona Prioritization'} defaultTitle='Persona Prioritization' onSave={handleSavePageTitle} onClose={() => setEditingPageTitle(false)} isLoading={isUpdatingPageTitle} />}
    </>
  )
}

export default Personas
